/**
 * Indicador de carga con un personaje que mueve los brazos.
 *
 * Idea del dueño: cuando algo tarda, que se vea claramente que la aplicación
 * está trabajando y no colgada. Antes cada pantalla resolvía esto por su
 * cuenta —un texto gris de «Cargando…» en la esquina, o nada— y una espera
 * larga se leía igual que una pantalla rota. Es exactamente lo que pasó con la
 * carga de sitios: dos minutos sin ninguna señal.
 *
 * Dos formas de usarlo:
 *
 *   <PantallaCargando texto="Cargando tu sitio…" />   pantalla completa
 *   <Cargando tamaño={40} />                          dentro de una tarjeta
 *
 * Los brazos giran en sentidos opuestos, que es lo que hace que se lea como
 * «moviéndose» y no como «tildado». La animación se apaga sola si el sistema
 * del usuario pide menos movimiento: para quien tiene sensibilidad al
 * movimiento, un muñeco agitándose es un problema real, no un detalle.
 */

import { useEffect, useState } from 'react';
import { suscribirActividad } from '@/lib/api';

const CSS = `
@keyframes cav-brazo-izq { 0%,100% { transform: rotate(-42deg) } 50% { transform: rotate(34deg) } }
@keyframes cav-brazo-der { 0%,100% { transform: rotate(42deg) } 50% { transform: rotate(-34deg) } }
@keyframes cav-rebote    { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5%) } }
@keyframes cav-sombra    { 0%,100% { transform: scaleX(1); opacity: .16 } 50% { transform: scaleX(.8); opacity: .09 } }
.cav-cuerpo   { animation: cav-rebote 1.1s ease-in-out infinite; transform-origin: 50% 100%; }
.cav-brazo-i  { animation: cav-brazo-izq 1.1s ease-in-out infinite; transform-origin: 27px 43px; }
.cav-brazo-d  { animation: cav-brazo-der 1.1s ease-in-out infinite; transform-origin: 73px 43px; }
.cav-sombra   { animation: cav-sombra 1.1s ease-in-out infinite; transform-origin: 50px 92px; }
@media (prefers-reduced-motion: reduce) {
  .cav-cuerpo, .cav-brazo-i, .cav-brazo-d, .cav-sombra { animation: none; }
}
`;

/**
 * El personaje.
 *
 * Los brazos van SEPARADOS del cuerpo a propósito. En el primer intento nacían
 * pegados a los costados y, al girar, quedaban ocultos detrás del torso: en
 * pantalla se veía una mancha oscura que no se movía. Se descubrió mirando la
 * captura, no leyendo el código.
 *
 * Tiene un tamaño mínimo real: por debajo de unos 40 px deja de leerse como
 * una persona y pasa a ser un punto. Por eso no se usa dentro de los botones
 * —ahí sigue el giro sencillo de siempre— y en su lugar, cuando una acción
 * tarda, aparece el aviso central, que sí tiene sitio para el muñeco.
 */
export function Cargando({ tamaño = 72, className = '', color = '#0F172A' }) {
  const chico = tamaño < 34;
  const grosorBrazo = chico ? 9 : 7;
  return (
    <>
      <style>{CSS}</style>
      <svg width={tamaño} height={tamaño} viewBox="0 0 100 100" className={className}
           role="img" aria-label="Cargando" data-testid="cav-cargando">
        {!chico && <ellipse className="cav-sombra" cx="50" cy="92" rx="20" ry="4" fill={color} />}
        <g className="cav-cuerpo">
          {/* Los brazos toman el color del negocio. `var(--cv-pink)` vale el
              rosa de casa por defecto y el color elegido dentro del panel del
              tenant, así que esto no necesita saber nada de la marca: hereda. */}
          <rect className="cav-brazo-i" x={27 - grosorBrazo / 2} y="40"
                width={grosorBrazo} height="30" rx={grosorBrazo / 2} fill="var(--cv-pink)" />
          <rect className="cav-brazo-d" x={73 - grosorBrazo / 2} y="40"
                width={grosorBrazo} height="30" rx={grosorBrazo / 2} fill="var(--cv-pink)" />
          <rect x="38" y="36" width="24" height="34" rx="10" fill={color} />
          <circle cx="50" cy={chico ? 20 : 21} r={chico ? 17 : 15} fill={color} />
          {!chico && (
            <>
              <circle cx="45.5" cy="21" r="2.8" fill="#fff" />
              <circle cx="54.5" cy="21" r="2.8" fill="#fff" />
              <path d="M45 28 Q50 32 55 28" stroke="#fff" strokeWidth="2.2"
                    strokeLinecap="round" fill="none" />
            </>
          )}
          <rect x="43" y="70" width="6" height="14" rx="3" fill={color} />
          <rect x="51" y="70" width="6" height="14" rx="3" fill={color} />
        </g>
      </svg>
    </>
  );
}

/**
 * Ocupa la pantalla y se pone al centro. Para esperas que bloquean: cargar
 * una pantalla entera, subir un sitio, publicar.
 *
 * `sobreTodo` la fija sobre el contenido con un velo; sin ella se centra
 * dentro del área donde se coloque, que es lo correcto cuando lo que carga es
 * solo un panel y el resto de la pantalla sigue siendo usable.
 */
export function PantallaCargando({ texto = 'Cargando…', detalle = '', sobreTodo = false }) {
  const contenedor = sobreTodo
    ? 'fixed inset-0 z-50 bg-white/80 backdrop-blur-sm'
    : 'w-full min-h-[320px]';
  return (
    <div className={`${contenedor} flex flex-col items-center justify-center gap-3`}
         role="status" aria-live="polite" data-testid="cav-pantalla-cargando">
      <Cargando tamaño={88} />
      <p className="text-sm font-medium text-[#0F172A]">{texto}</p>
      {detalle && <p className="text-xs text-[#64748B] max-w-xs text-center">{detalle}</p>}
    </div>
  );
}

/**
 * Red de seguridad: aparece solo cuando algo tarda y nadie más está avisando.
 *
 * Las pantallas ya muestran su propio indicador mientras cargan, pero hay
 * decenas de acciones sueltas —guardar, filtrar, pedirle algo a la IA— que no
 * tienen ninguna señal. Esto se engancha al cliente de peticiones: si hay
 * trabajo en curso más de `RETARDO` milisegundos, sale el muñequito.
 *
 * Dos decisiones que importan:
 *
 *   * El retardo. Sin él, cada petición de 80 ms haría parpadear el muñequito
 *     por toda la pantalla, que se siente peor que no tener nada.
 *   * No bloquea. `pointer-events: none` y sin velo: es un aviso, no una
 *     puerta. Si algo se cuelga, el usuario sigue pudiendo usar la pantalla en
 *     vez de quedar atrapado detrás de una animación.
 */
const RETARDO = 600;

export function CargaGlobal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let temporizador = null;
    const cancelar = suscribirActividad((activas) => {
      if (activas > 0) {
        if (!temporizador) temporizador = setTimeout(() => setVisible(true), RETARDO);
      } else {
        if (temporizador) { clearTimeout(temporizador); temporizador = null; }
        setVisible(false);
      }
    });
    return () => { if (temporizador) clearTimeout(temporizador); cancelar(); };
  }, []);

  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
         role="status" aria-live="polite" data-testid="cav-carga-global">
      <div className="flex items-center gap-3 rounded-2xl bg-white/95 shadow-lg
                      ring-1 ring-black/5 px-5 py-3">
        <Cargando tamaño={44} />
        <span className="text-sm font-medium text-[#0F172A]">Trabajando…</span>
      </div>
    </div>
  );
}

export default Cargando;
